/**
 * Field catalog for the automation condition-builder.
 *
 * Each trigger has its own list of fields that the user can filter on.
 * The `type` drives which operators are offered (see OPERATOR_CATALOG).
 *
 * `path` is the dot-path in the runtime source-object that the backend
 * evaluates against. Paths are passed directly to the API as `condition.field`.
 *
 * Dynamic fields (form element fields for form_submission) are added at
 * runtime by the ConditionConfig component based on the selected formId.
 */

export const FIELD_TYPES = {
  STRING: 'string',
  NUMBER: 'number',
  ENUM: 'enum',
  UUID: 'uuid',
  DATE: 'date',
  ANY: 'any',
};

const COMMON_DEAL_FIELDS = [
  { path: 'deal.value', label: 'Deal-waarde', type: FIELD_TYPES.NUMBER },
  { path: 'deal.currency', label: 'Valuta', type: FIELD_TYPES.ENUM, options: ['EUR', 'USD', 'GBP'] },
  { path: 'deal.title', label: 'Titel', type: FIELD_TYPES.STRING },
  {
    path: 'deal.priority',
    label: 'Prioriteit',
    type: FIELD_TYPES.ENUM,
    options: ['hot', 'warm', 'cold'],
  },
  { path: 'deal.source', label: 'Bron (deal)', type: FIELD_TYPES.STRING },
  { path: 'deal.expected_close_date', label: 'Verwachte sluitdatum', type: FIELD_TYPES.DATE },
  { path: 'lead.email', label: 'Lead e-mail', type: FIELD_TYPES.STRING },
  { path: 'lead.company_name', label: 'Bedrijfsnaam', type: FIELD_TYPES.STRING },
  { path: 'lead.name', label: 'Lead naam', type: FIELD_TYPES.STRING },
  { path: 'pipeline.id', label: 'Pipeline', type: FIELD_TYPES.UUID },
  { path: 'stage.id', label: 'Huidige stage', type: FIELD_TYPES.UUID },
];

export const TRIGGER_FIELD_CATALOG = {
  deal_created: [
    ...COMMON_DEAL_FIELDS,
    {
      path: 'source',
      label: 'Bron van creatie',
      type: FIELD_TYPES.ENUM,
      options: ['form', 'manual', 'api'],
    },
  ],
  deal_status_change: [
    ...COMMON_DEAL_FIELDS,
    { path: 'from_stage.id', label: 'Vorige stage', type: FIELD_TYPES.UUID },
    { path: 'to_stage.id', label: 'Nieuwe stage', type: FIELD_TYPES.UUID },
    {
      path: 'status_type',
      label: 'Status-type',
      type: FIELD_TYPES.ENUM,
      options: ['open', 'won', 'lost'],
    },
  ],
  form_submission: [
    { path: 'submission.form_id', label: 'Form-ID', type: FIELD_TYPES.UUID },
    { path: 'submission.lead_id', label: 'Lead-ID', type: FIELD_TYPES.UUID },
  ],
  lead_status_change: [
    { path: 'lead.email', label: 'E-mail', type: FIELD_TYPES.STRING },
    { path: 'lead.company_name', label: 'Bedrijfsnaam', type: FIELD_TYPES.STRING },
    { path: 'lead.name', label: 'Lead naam', type: FIELD_TYPES.STRING },
    { path: 'lead.status', label: 'Status', type: FIELD_TYPES.ENUM },
    { path: 'from_status', label: 'Vorige status', type: FIELD_TYPES.ENUM },
    { path: 'to_status', label: 'Nieuwe status', type: FIELD_TYPES.ENUM },
  ],
  status_change: [
    { path: 'lead.email', label: 'E-mail', type: FIELD_TYPES.STRING },
    { path: 'lead.company_name', label: 'Bedrijfsnaam', type: FIELD_TYPES.STRING },
    { path: 'lead.name', label: 'Lead naam', type: FIELD_TYPES.STRING },
    { path: 'lead.status', label: 'Status', type: FIELD_TYPES.ENUM },
    { path: 'from_status', label: 'Vorige status', type: FIELD_TYPES.ENUM },
    { path: 'to_status', label: 'Nieuwe status', type: FIELD_TYPES.ENUM },
  ],
  proposal_accepted: [
    { path: 'proposal.total_amount', label: 'Offertebedrag', type: FIELD_TYPES.NUMBER },
    { path: 'proposal.proposal_type', label: 'Offerte-type', type: FIELD_TYPES.ENUM, options: ['definitive', 'proforma', 'additional'] },
    { path: 'lead.email', label: 'Lead e-mail', type: FIELD_TYPES.STRING },
  ],
  proposal_rejected: [
    { path: 'proposal.total_amount', label: 'Offertebedrag', type: FIELD_TYPES.NUMBER },
    { path: 'proposal.proposal_type', label: 'Offerte-type', type: FIELD_TYPES.ENUM, options: ['definitive', 'proforma', 'additional'] },
    { path: 'lead.email', label: 'Lead e-mail', type: FIELD_TYPES.STRING },
    { path: 'proposal.reject_reason', label: 'Afwijsreden', type: FIELD_TYPES.STRING },
    {
      path: 'proposal.reject_reason_code',
      label: 'Afwijsreden-code',
      type: FIELD_TYPES.ENUM,
      options: [
        'too_expensive',
        'other_quote',
        'other_provider',
        'postponed',
        'not_a_fit',
        'lead_time',
        'other',
      ],
    },
  ],
  proposal_status_change: [
    { path: 'from_status', label: 'Vorige status', type: FIELD_TYPES.ENUM, options: ['draft', 'sent', 'viewed', 'accepted', 'rejected', 'withdrawn'] },
    { path: 'to_status', label: 'Nieuwe status', type: FIELD_TYPES.ENUM, options: ['draft', 'sent', 'viewed', 'accepted', 'rejected', 'withdrawn'] },
    { path: 'proposal.proposal_type', label: 'Offerte-type', type: FIELD_TYPES.ENUM, options: ['definitive', 'proforma', 'additional'] },
    { path: 'proposal.total_amount', label: 'Offertebedrag', type: FIELD_TYPES.NUMBER },
    { path: 'lead.email', label: 'Lead e-mail', type: FIELD_TYPES.STRING },
  ],
  project_status_change: [
    { path: 'from_status', label: 'Vorige status', type: FIELD_TYPES.ENUM, options: ['draft', 'planning', 'active', 'completed', 'cancelled'] },
    { path: 'to_status', label: 'Nieuwe status', type: FIELD_TYPES.ENUM, options: ['draft', 'planning', 'active', 'completed', 'cancelled'] },
    { path: 'project.title', label: 'Projecttitel', type: FIELD_TYPES.STRING },
    { path: 'project.status', label: 'Projectstatus', type: FIELD_TYPES.ENUM, options: ['draft', 'planning', 'active', 'completed', 'cancelled'] },
    { path: 'lead.email', label: 'Lead e-mail', type: FIELD_TYPES.STRING },
    { path: 'deal.value', label: 'Deal-waarde', type: FIELD_TYPES.NUMBER },
  ],
  project_fully_completed: [
    { path: 'project.title', label: 'Projecttitel', type: FIELD_TYPES.STRING },
    { path: 'lead.email', label: 'Lead e-mail', type: FIELD_TYPES.STRING },
    { path: 'deal.value', label: 'Deal-waarde', type: FIELD_TYPES.NUMBER },
  ],
  work_order_completed: [
    { path: 'work_order.work_order_type', label: 'Werkbon-type', type: FIELD_TYPES.ENUM, options: ['installation', 'measurement', 'service'] },
    { path: 'work_order.photo_count', label: 'Aantal foto\'s', type: FIELD_TYPES.NUMBER },
    { path: 'installer.name', label: 'Monteur(s) deze werkbon', type: FIELD_TYPES.STRING },
    { path: 'visit.crew', label: 'Ploeg hele bezoek', type: FIELD_TYPES.STRING },
    { path: 'visit.is_multi_day', label: 'Meerdaags bezoek', type: FIELD_TYPES.ENUM, options: ['true', 'false'] },
    { path: 'visit.is_last_on_project', label: 'Laatste werkbon op project', type: FIELD_TYPES.ENUM, options: ['true', 'false'] },
    { path: 'project.title', label: 'Projecttitel', type: FIELD_TYPES.STRING },
    { path: 'lead.email', label: 'Lead e-mail', type: FIELD_TYPES.STRING },
  ],
};

TRIGGER_FIELD_CATALOG.work_visit_confirmed = [
  { path: 'work_order.work_order_type', label: 'Werkbon-type', type: FIELD_TYPES.ENUM, options: ['installation', 'measurement', 'service'] },
  { path: 'visit.is_multi_day', label: 'Meerdaags bezoek', type: FIELD_TYPES.ENUM, options: ['true', 'false'] },
  { path: 'visit.day_count', label: 'Aantal dagen', type: FIELD_TYPES.NUMBER },
  { path: 'visit.crew', label: 'Ploeg hele bezoek', type: FIELD_TYPES.STRING },
  { path: 'project.title', label: 'Projecttitel', type: FIELD_TYPES.STRING },
  { path: 'lead.email', label: 'Lead e-mail', type: FIELD_TYPES.STRING },
];

TRIGGER_FIELD_CATALOG.work_visit_completed = [
  ...TRIGGER_FIELD_CATALOG.work_order_completed,
];

TRIGGER_FIELD_CATALOG.project_all_work_orders_completed = [
  ...TRIGGER_FIELD_CATALOG.work_order_completed,
];

export const OPERATOR_CATALOG = [
  {
    value: 'equals',
    label: 'is gelijk aan',
    supports: [FIELD_TYPES.STRING, FIELD_TYPES.NUMBER, FIELD_TYPES.ENUM, FIELD_TYPES.UUID, FIELD_TYPES.DATE],
    requiresValue: true,
  },
  {
    value: 'not_equals',
    label: 'is niet gelijk aan',
    supports: [FIELD_TYPES.STRING, FIELD_TYPES.NUMBER, FIELD_TYPES.ENUM, FIELD_TYPES.UUID, FIELD_TYPES.DATE],
    requiresValue: true,
  },
  {
    value: 'contains',
    label: 'bevat',
    supports: [FIELD_TYPES.STRING],
    requiresValue: true,
  },
  {
    value: 'not_contains',
    label: 'bevat niet',
    supports: [FIELD_TYPES.STRING],
    requiresValue: true,
  },
  {
    value: 'gt',
    label: 'groter dan',
    supports: [FIELD_TYPES.NUMBER, FIELD_TYPES.DATE],
    requiresValue: true,
    numeric: true,
  },
  {
    value: 'gte',
    label: 'groter dan of gelijk',
    supports: [FIELD_TYPES.NUMBER, FIELD_TYPES.DATE],
    requiresValue: true,
    numeric: true,
  },
  {
    value: 'lt',
    label: 'kleiner dan',
    supports: [FIELD_TYPES.NUMBER, FIELD_TYPES.DATE],
    requiresValue: true,
    numeric: true,
  },
  {
    value: 'lte',
    label: 'kleiner dan of gelijk',
    supports: [FIELD_TYPES.NUMBER, FIELD_TYPES.DATE],
    requiresValue: true,
    numeric: true,
  },
  {
    value: 'is_empty',
    label: 'is leeg',
    supports: 'any',
    requiresValue: false,
  },
  {
    value: 'is_not_empty',
    label: 'is niet leeg',
    supports: 'any',
    requiresValue: false,
  },
];

/**
 * Legacy operator aliases. Backend accepts both, but we normalize to the
 * canonical form on load so the UI shows a consistent option.
 */
export const OPERATOR_ALIASES = {
  greater_than: 'gt',
  less_than: 'lt',
  greater_than_or_equal: 'gte',
  less_than_or_equal: 'lte',
  equal: 'equals',
  not_equal: 'not_equals',
};

export function normalizeOperator(op) {
  if (!op) return 'equals';
  return OPERATOR_ALIASES[op] || op;
}

export function getFieldsForTrigger(triggerType) {
  return TRIGGER_FIELD_CATALOG[triggerType] || [];
}

export function getOperatorsForFieldType(fieldType) {
  if (!fieldType) return OPERATOR_CATALOG;
  return OPERATOR_CATALOG.filter((op) => op.supports === 'any' || op.supports.includes(fieldType));
}

export function operatorRequiresValue(operatorValue) {
  const op = OPERATOR_CATALOG.find((o) => o.value === operatorValue);
  return op ? op.requiresValue : true;
}

export function isNumericOperator(operatorValue) {
  const op = OPERATOR_CATALOG.find((o) => o.value === operatorValue);
  return !!op?.numeric;
}
